// src/components/SurveyForm/Questions/SelectQuestion/SelectQuestion.jsx

import React from 'react';
import {
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

/**
 * Componente para preguntas de tipo Select (dropdown)
 * Soporta carga de opciones desde API
 */
const SelectQuestion = ({
  questionName,
  description,
  options = [],
  currentValue = '',
  questionId,
  typeQuestion,
  index,
  urlParam,
  hasError = false,
  questionRef,
  onValueChange,
  hasOwnBold,
}) => {
  /**
   * Maneja el cambio de selección
   * @param {Event} event - Evento del select
   */
  const handleChange = (event) => {
    const selectedValue = event.target.value;

    // Notificar el cambio de valor
    onValueChange(selectedValue);

  };

  /**
   * Verifica si el select debe estar deshabilitado
   */
  const isDisabled = options.length === 0;

  return (
    <FormControl
      fullWidth
      error={hasError}
      ref={questionRef}
    >
      <InputLabel id={`${questionId}-${typeQuestion}-label`}>
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questionName) }} />
      </InputLabel>

      {/* Descripción opcional */}
      {description && (
        <Typography
          variant="caption"
          style={{
            display: 'block',
            fontStyle: 'italic',
            marginBottom: '0.5em',
            color: hasError ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
          }}
        >
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
        </Typography>
      )}

      <Select
        labelId={`${questionId}-${typeQuestion}-label`}
        id={`${questionId}-${typeQuestion}`}
        value={currentValue}
        label={questionName}
        onChange={handleChange}
        disabled={isDisabled}
        error={hasError}
        sx={{
          backgroundColor: hasError ? '#ffe6e6' : 'white',
        }}
      >
        {/* Opción placeholder si está vacío */}
        {currentValue === '' && (
          <MenuItem value="" disabled>
            <em>Selecciona una opción</em>
          </MenuItem>
        )}

        {/* Opciones disponibles */}
        {options.map(({ numberOption, optionName }) => (
          <MenuItem
            key={numberOption}
            value={optionName}
            translate="no"
          >
            {optionName}
          </MenuItem>
        ))}
      </Select>

      {/* Mensaje de error */}
      {hasError && (
        <Typography
          variant="caption"
          style={{
            color: '#d32f2f',
            marginTop: '0.5em',
            fontWeight: 500,
          }}
        >
          Este campo es requerido
        </Typography>
      )}

      {/* Mensaje cuando no hay opciones */}
      {isDisabled && (
        <Typography
          variant="caption"
          style={{
            color: 'rgba(0, 0, 0, 0.6)',
            marginTop: '0.5em',
            fontStyle: 'italic',
          }}
        >
          No hay opciones disponibles
        </Typography>
      )}

      <Divider variant="middle" sx={{ marginTop: '1em' }} />
    </FormControl>
  );
};

SelectQuestion.propTypes = {
  questionName: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      numberOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      optionName: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentValue: PropTypes.string,
  questionId: PropTypes.number.isRequired,
  typeQuestion: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  urlParam: PropTypes.string,
  hasError: PropTypes.bool,
  questionRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onValueChange: PropTypes.func.isRequired,
};

export default SelectQuestion;